import type { SlideImage } from "yet-another-react-lightbox";
import {
  ContentType,
  EventCategoryType,
  GalleryVotingType,
  ImageOrientationType,
  MatchState,
  SportsTickerEventType,
} from "./enums";
import { PageInformationInterface, TrackingInformationInterface } from "./PageInformationInterface";

export interface LinkEntry {
  rel: string;
  name?: string;
  href: string;
}

export interface LinksObject {
  links: LinkEntry[];
}

export interface Author extends LinksObject {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  shortName?: string;
  ressort: string;
  images: ImageEntry[];
}

export interface ImageEntry extends LinksObject {
  id: string;
  title: string;
  type: ContentType;
  credit: string;
  rate?: number;
  votes?: number;
  subline: string;
  alignment: ImageOrientationType;
}

export interface ResponseEntry {
  status: number;
  pageInformation?: PageInformationInterface;
}

export interface JsonResponseEntry extends ResponseEntry {
  json?: any;
}

export interface PersonalNavigationEntry extends LinksObject {
  id: string;
  title: string;
}

export interface NavigationEntry
  extends ResponseEntry,
    PersonalNavigationEntry {
  linkedId: string;
  extendable: boolean;
  loading: boolean;
  isOpen?: boolean;
  children: NavigationEntry[];
  trendingTopics: TeaserEntry[];
}

export interface TeaserEntry extends LinksObject {
  id: string;
  creationDate: string;
  date: number;
  type: ContentType;
  title: string;
  ressort: string;
  topline: string;
  subline: string;
  lead: string;
  body: string;
  modulePatch: string;
  images: ImageEntry[];
  paid: boolean;
  authors: Author[];
  featured: boolean;
  minimizedView?: boolean;
  teaserTypes?: string[];
  highlight?: boolean;
  imageUrl?: string;
  adIdentifier: boolean;
  hidePlusIcon: boolean;
  breakingNewsIdentifier?: boolean;
  // Gallery configured in the CMS to show as an in-article slideshow (NNBAP-575).
  sliderView?: boolean;
}

export interface CommentSection {
  comments: CommentEntry[];
  loadMore?: boolean;
  commentsCount: number;
  disableComments: boolean;
}

export interface CommentEntry {
  author: string;
  date?: number;
  creationDate: string;
  text: string;
}

export interface CommentUrl {
  identifier: string;
  url: string;
}

export interface LinkedTeaserEntry {
  linkButtonText: string;
  linkButtonUrl?: string;
}

export interface ImageTeaserEntry extends TeaserEntry {
  image?: ImageEntry;
}

export interface AdsEntry {
  id: string;
  className: string;
}
export interface AdvertTeaserEntry extends TeaserEntry {
  adType: string;
  position: number;
  ads: AdsEntry;
}

export interface AudioTeaserEntry extends TeaserEntry, LinkedTeaserEntry {
  audioUrl: string;
}

export interface ProviderTeaserEntry extends TeaserEntry {
  providerId: string;
}

export interface IntegrationTeaserEntry extends TeaserEntry {
  integrationId: string;
}

export interface VideoTeaserEntry extends ProviderTeaserEntry, IntegrationTeaserEntry {
}

export interface DailyMotionVideoTeaserEntry extends ProviderTeaserEntry {}

export interface FreeTextTeaserEntry extends TeaserEntry, LinkedTeaserEntry {}

export interface AmazonAffiliateBoxProductEntry extends LinksObject {
  links: LinkEntry[];
  images: LinksObject[];
  originalPrice?: string;
  price?: string;
  savingsPercentage?: number;
  savings?: string;
  title: string;
  partner?: string;
}
export interface AmazonAffiliateBoxTeaserEntry extends TeaserEntry {
  products: AmazonAffiliateBoxProductEntry[]; 
  teaserImage: boolean;
  originalType: string | null;
}

export interface QualifioTeaserEntry extends ProviderTeaserEntry {
  div: string;
}

export interface LivetickerTeaserEntry extends ProviderTeaserEntry {
  themeId?: string;
  historicalTicker?: boolean;
}

export interface KickerMatchdayTeaserEntry extends TeaserEntry {
  sportsTicker?: SportsTicker | null;
  sportsMatches?: SportsMatches | null;
  sportsTables?: SportsTable[] | null;
}

export interface CollectiveSwipeEntry extends LinkedTeaserEntry {
  imageUrl: string;
}

export interface CollectiveBoxTeaserEntry extends TeaserEntry {
  teasers: CollectiveSwipeEntry[];
}

export interface InfoBoxTeaserEntry extends TeaserEntry {
  showHrLines: boolean;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export interface FaqTeaserEntry extends TeaserEntry {
  faqs: FaqEntry[];
}

export interface LinkListEntry extends TeaserEntry {
}

export interface LinkBoxTeaserEntry extends TeaserEntry {
  linkList: LinkListEntry[];
}

export interface SearchModuleOption {
  label: string;
  type: string;
  param: string;
  values?: string[];
}

export interface RessortModuleEntry extends LinksObject {
  id: string;
  loading?: boolean;
  type: ContentType;
  title?: string;
  elementsViewType: string;
  contrastColor: boolean;
  loadMore: boolean;
  teasers: TeaserEntry[];
  featured: boolean;
  searchModuleOptions?: SearchModuleOption[];
  frame: boolean;
  icon?: string;
  highlight?: boolean;
  withLeadStory?: boolean;
  dataLinks?: LinkEntry[];
  autoContentSize?: number;
  linkList?: TeaserEntry[];
  page?: number;
  pianoRecoUrl?: string;
  pianoRecoExludeIds?: string[];
}

export interface RessortCategorySubEntryAttribute {
  key: string;
  value: string;
}
export interface RessortCategorySubEntry {
  externalId: string;
  name: string;
  publicationMode: string;
  confirmationListId: string;
  newsletterId: string;
}

export interface RessortCategoryMainEntry {
  externalId: string;
  name: string;
  entries: RessortCategorySubEntry[];
}

export interface RessortCategoryEntry {
  externalId: string;
  name: string;
  entries: RessortCategoryMainEntry[];
}

export interface RessortEntry extends ResponseEntry {
  modules: RessortModuleEntry[];
  categories: RessortCategoryEntry[];
  breakingNews: TeaserEntry[];
}

export interface SportsTeam {
  name: string;
  icon: string;
}

export interface SportsTickerScore {
  score: string;
  halfTimeScore?: string | null;
  subline?: string | null;
  state: MatchState;
}

export interface SportsTickerEvent {
  minute: number;
  additionalMinute?: number | null;
  headline?: string | null;
  subline?: string | null;
  text?: string | null;
  type: SportsTickerEventType;
  team?: SportsTeam | null;
  score?: string | null;
  home?: string | null;
  away?: string | null;
}

export interface ScoreBoardInfo {
  saison: string;
  matchday?: string | null;
}

export interface SportsTickerHeader {
  tournament: string;
  scoreBoardInfo: ScoreBoardInfo;
  homeTeam: SportsTeam;
  awayTeam: SportsTeam;
  score: SportsTickerScore;
  goals: SportsTickerEvent[];
  matchDate?: string;
}

export interface SportsTicker {
  header?: SportsTickerHeader | null;
  events?: SportsTickerEvent[] | null;
  lineup?: SportsLineup | null;
}

export interface SportsLineupSubstitution {
  playerIn: string;
  playerOut: string;
  minute: number;
  additionalMinute?: number;
}

export interface SportsTeamLineup {
  headline?: string | null;
  team: SportsTeam;
  lineUp?: string | null;
  substitution?: string[] | null;
  bench?: string | null;
  coach: string | null;
}

export interface SportsLineup {
  headline?: string | null;
  home: SportsTeamLineup;
  away: SportsTeamLineup;
}

export interface SportsMatch {
  id?: string;
  articleId?: string;
  homeTeam: SportsTeam;
  awayTeam: SportsTeam;
  minute?: number | null;
  additionalMinute?: number | null;
  score?: SportsTickerScore | null;
  matchDate?: string | null;
}
export interface SportsMatches {
  headline?: string | null;
  topline?: string | null;
  matches: SportsMatch[];
}

export interface SportsTableEntry {
  team: SportsTeam;
  position: number;
  matches: number;
  points: number;
  goalDifference: number;
}

export interface SportsTable {
  headline: string;
  entries: SportsTableEntry[];
}

export interface Article extends ResponseEntry, LinksObject {
  id: string
  date: number
  creationDate: string
  type: ContentType
  title: string
  topline: string
  body: string
  lead: string
  headerImage: TeaserEntry | ImageEntry
  images: TeaserEntry[] | ImageEntry[]
  authors: Author[]
  location: string
  paid?: boolean | null;
  objectListTeasers: TeaserEntry[]
  sharingUrl?: string
  objectComments: CommentSection
  showSocialToolbar: boolean
  linkedCategories: EventCategoryEntry[]
  sportsTicker?: SportsTicker | null;
  sportsMatches?: SportsMatches | null;
  sportsTables?: SportsTable[] | null;
  teaserTypes?: string[] | null;
  adIdentifier?: boolean
  breakingNewsIdentifier?: boolean
  modules?: RessortModuleEntry[] | null;
}

export interface GalleryVoting {
  hideResult?: boolean;
  isWithDoiMail?: boolean;
  lead: string;
  scaleTo: number;
  type: GalleryVotingType;
}

export interface GalleryArticle extends Article {
  voting?: GalleryVoting;
}

export interface LinkObject {
  articleId?: string;
  ressortId?: string;
  externalLink?: string;
  internalLink?: string;
}

export interface EventCategoryEntry extends LinksObject {
  id: string;
  name: string;
  title: string;
  link?: string;
  type?: EventCategoryType;
  linkObject: LinkObject;
}

export interface AudioPlayer {
  title: string;
  topline: string;
  audioUrl: string;
  playing: boolean;
  imageUrl?: string;
}

export interface Subscription {
  purApp: boolean;
  nnPlusApp: boolean;
  purWeb: boolean;
  nnPlusWeb: boolean;
  validated: boolean;
  purValidUntil: any;
  nnPlusValidUntil: any;
  nnPlusTermId: string;
  nnPlusReceiptData: string;
  purTermId: string;
  purReceiptData: string;
}

export interface SearchResult extends ResponseEntry {
  pageInformation: PageInformationInterface;
  showLoadMore: boolean;
  teasers: TeaserEntry[];
}

export enum Platform {
  Unknown = 'unknown',
  iOS = 'ios',
  Android = 'android',
}

export function parsePlatform(value: unknown): Platform {
  switch (String(value ?? '').toLowerCase()) {
    case 'ios':
      return Platform.iOS;
    case 'android':
      return Platform.Android;
    default:
      return Platform.Unknown;
  }
}

export interface AppInfo {
  name: string;
  version: string;
  platform: Platform;
  pushesEnabled: boolean;
  pushSubscriptionId: string;
  iapAvailable: boolean;
}

export interface CmpMappingEntry {
  identifier: string;
  vendorId: string;
}

export interface CmpMappingResponse {
  setCmpMappingWithFrameSize: string[];
  cmpVendorIdMapping: Record<string, string>;
}

export interface CatchlineTeaserEntry extends TeaserEntry {
  teasers: TeaserEntry[];
}

export interface VoteEntry {
  id: string;
  rate?: number;
  votes?: number;
}

export interface PreviousVote {
  articleId: string;
  votes: VoteEntry[];
}

export interface AndroidPurchase {
  orderId: string;
  packageName: string;
  productId: string;
  purchaseTime: number;
  purchaseState: number;
  purchaseToken: string;
  quantity: number;
  autoRenewing: boolean;
  acknowledged: boolean;
  newPurchase: boolean;
  trackingInformation: TrackingInformationInterface | string;

}

export type ExtendedSlideImage = SlideImage & { description?: string; credit?: string };
